<template>
  <div
    ref="windowEl"
    class="mac-window absolute bg-gray-200 border-2 border-black shadow-pixel flex flex-col overflow-hidden animate-mac-window-open transition-shadow duration-200 hover:shadow-pixel-sm"
    :data-window-id="props.id"
    :style="windowStyle"
    @pointerdown="onWindowPointerDown"
  >
    <!-- Double border: 2px black outer, 2px white gap, 1px black inner -->
    <div class="absolute pointer-events-none border border-black" style="inset: 2px;" />
    <TitleBar
      :title="title"
      :dragging="isDragging"
      @close="$emit('close')"
      @drag-start="startDrag"
    />
    <div class="mac-window-content flex-1 overflow-auto p-2 sm:p-4 border-t-2 border-black bg-white min-h-0">
      <slot />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import TitleBar from './TitleBar.vue'

const props = defineProps({
  id: { type: [String, Number], default: null },
  title: { type: String, required: true },
  width: { type: Number, default: 900 },
  minHeight: { type: Number, default: 630 },
  initialPosition: {
    type: Object,
    default: () => ({ x: 80, y: 60 }),
  },
  zIndex: { type: Number, default: 1 },
})

defineEmits(['close'])

const windowEl = ref(null)
const position = reactive({
  x: props.initialPosition.x,
  y: props.initialPosition.y,
})

/** Fit within viewport; inner area scrolls via .mac-window-content */
const windowStyle = computed(() => ({
  left: `${position.x}px`,
  top: `${position.y}px`,
  width: `min(${props.width}px, calc(100vw - 16px))`,
  minHeight: `min(${props.minHeight}px, calc(100dvh - 3.5rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)))`,
  maxHeight: `calc(100dvh - 3.5rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))`,
  zIndex: props.zIndex,
}))

function getParentBounds() {
  const el = windowEl.value
  if (!el) {
    return {
      w: typeof window !== 'undefined' ? window.innerWidth : 0,
      h: typeof window !== 'undefined' ? Math.max(0, window.innerHeight - 48) : 0,
    }
  }
  const p = el.offsetParent
  if (p instanceof HTMLElement) {
    return { w: p.clientWidth, h: p.clientHeight }
  }
  return {
    w: window.innerWidth,
    h: Math.max(0, window.innerHeight - 48),
  }
}

function clampPosition() {
  const el = windowEl.value
  if (!el) return
  const { w, h } = getParentBounds()
  const rw = el.offsetWidth
  const rh = el.offsetHeight
  position.x = Math.max(0, Math.min(position.x, Math.max(0, w - rw)))
  position.y = Math.max(0, Math.min(position.y, Math.max(0, h - rh)))
}

let resizeRaf = 0
function onResizeClamp() {
  cancelAnimationFrame(resizeRaf)
  resizeRaf = requestAnimationFrame(() => {
    clampPosition()
  })
}

const isDragging = ref(false)
let dragStart = { x: 0, y: 0, posX: 0, posY: 0 }
/** @type {number | null} */
let dragPointerId = null

function bringToFront() {
  if (props.id != null) {
    window.dispatchEvent(new CustomEvent('mac-window-focus', { detail: { id: props.id } }))
  }
}

function onWindowPointerDown(e) {
  if (e.pointerType === 'mouse' && e.button !== 0) return
  bringToFront()
}

function startDrag(e) {
  if (e.pointerType === 'mouse' && e.button !== 0) return
  isDragging.value = true
  dragPointerId = e.pointerId
  dragStart.x = e.clientX
  dragStart.y = e.clientY
  dragStart.posX = position.x
  dragStart.posY = position.y
  document.addEventListener('pointermove', onDrag, { passive: false })
  document.addEventListener('pointerup', stopDrag)
  document.addEventListener('pointercancel', stopDrag)
}

function onDrag(e) {
  if (dragPointerId != null && e.pointerId !== dragPointerId) return
  position.x = dragStart.posX + (e.clientX - dragStart.x)
  position.y = dragStart.posY + (e.clientY - dragStart.y)
  const el = windowEl.value
  const { w, h } = getParentBounds()
  position.x = Math.max(0, position.x)
  position.y = Math.max(0, position.y)
  if (el) {
    position.x = Math.min(position.x, Math.max(0, w - el.offsetWidth))
    position.y = Math.min(position.y, Math.max(0, h - el.offsetHeight))
  }
}

function stopDrag(e) {
  if (e && dragPointerId != null && e.pointerId !== dragPointerId) return
  isDragging.value = false
  dragPointerId = null
  document.removeEventListener('pointermove', onDrag)
  document.removeEventListener('pointerup', stopDrag)
  document.removeEventListener('pointercancel', stopDrag)
  nextTick(() => clampPosition())
}

defineExpose({ bringToFront })

onMounted(() => {
  nextTick(() => {
    clampPosition()
  })
  window.addEventListener('resize', onResizeClamp)
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', onResizeClamp)
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', onResizeClamp)
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', onResizeClamp)
  }
  cancelAnimationFrame(resizeRaf)
})
</script>
